// Quick Create Flow — the same estimate / invoice / payment / signature flow
// used in both the Tech Portal and the Owner shell (as a floating action
// button). Designed to chain: after saving an estimate, you get one-tap "Now
// invoice it" → "Now collect payment" → done. No screen-hopping.
//
// Three ways this gets mounted:
//   1. <QuickCreatePanel/> — inline view inside Tech Portal "+ Create" tab
//   2. <QuickCreateFab/>   — floating button in the owner shell, opens drawer
//   3. <QuickCreateDrawer/>— manually controlled (e.g. from Contact profile)

import React, { useEffect, useRef, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Spinner, Field, Input, Textarea, Select } from '../ui.jsx';
import { ContactPicker } from './ContactPicker.jsx';
import { PriceBookPicker } from './PriceBookPicker.jsx';

// ─── Inline panel (what Tech Portal uses) ───────────────────────────────────
export function QuickCreatePanel({ toast, onCreated, initialContact = null, initialAction = null, techMode = false }) {
  const [action, setAction] = useState(initialAction);
  // Carry context between steps so "convert to invoice" pre-fills with the
  // estimate's customer + line items.
  const [carry, setCarry] = useState({ contact: initialContact || {}, lines: null, lastSavedKind: null, lastSavedId: null });

  if (!action) return <CreateTilesScreen onPick={setAction} techMode={techMode}/>;

  const props = {
    toast,
    carry,
    onClose: () => { setAction(null); onCreated?.(); },
    onSaved: (next) => {
      // Update carry + decide next step. If `next` includes a recommended action,
      // chain to it; otherwise return to the tile screen.
      setCarry(next?.carry ? { ...carry, ...next.carry } : carry);
      if (next?.nextAction) setAction(next.nextAction);
      else { setAction(null); onCreated?.(); }
    },
  };
  if (action === 'estimate') return <EstimateForm {...props}/>;
  if (action === 'invoice')  return <InvoiceForm {...props}/>;
  if (action === 'payment')  return <PaymentForm {...props}/>;
  if (action === 'signature')return <SignatureForm {...props}/>;
  return null;
}

function CreateTilesScreen({ onPick, techMode = false }) {
  // Techs only get customer-facing close + collect tools. Invoice authoring lives
  // with owners/admins — techs collect payment via the 'payment' tile, which
  // auto-creates the invoice behind the scenes.
  const allTiles = [
    { id:'estimate',  icon:'📄', label:'New Estimate',   sub:'Quote a customer' },
    { id:'estimate',  icon:'📋', label:'New Proposal',   sub:'Good / Better / Best tiers' },
    { id:'invoice',   icon:'💰', label:'New Invoice',    sub:'Bill for work done', ownerOnly:true },
    { id:'payment',   icon:'💳', label:'Collect Payment',sub:'Stripe pay link via text' },
    { id:'signature', icon:'✍️', label:'Get Signature',  sub:'Sign on the customer\'s phone' },
  ];
  const tiles = techMode ? allTiles.filter(t => !t.ownerOnly) : allTiles;
  return <div>
    <h2 style={{ margin:'0 0 14px 0', fontSize:20 }}>What do you want to create?</h2>
    <div style={{ display:'grid', gap:10 }}>
      {tiles.map((t, i) => (
        <div key={i} onClick={()=>onPick(t.id)} style={{ ...styles.card, padding:16, display:'flex', alignItems:'center', gap:14, cursor:'pointer' }}>
          <div style={{ fontSize:32 }}>{t.icon}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:15 }}>{t.label}</div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{t.sub}</div>
          </div>
          <div style={{ fontSize:18, color:G.textMuted }}>›</div>
        </div>
      ))}
    </div>
  </div>;
}

// ─── Estimate ───────────────────────────────────────────────────────────────
// Every estimate ships with Good / Better / Best tiers. The user adds line
// items as the "Good" baseline; the Better and Best tiers are auto-built from
// it on save (server seeds them via tier_builder). The customer picks one to
// approve, and that tier becomes the invoice.
const PAYMENT_TERMS_OPTIONS = ['Due on receipt', 'Net 7', 'Net 14', 'Net 30', 'Net 60'];

function EstimateForm({ toast, carry, onClose, onSaved }) {
  const [contact, setContact] = useState(carry.contact || {});
  const [title, setTitle] = useState('');
  const [lines, setLines] = useState(carry.lines || [{ name:'', qty:1, price:0 }]);
  const [taxRate, setTaxRate] = useState(7);
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedEstimate, setSavedEstimate] = useState(null); // shown after save
  const [previewTiers, setPreviewTiers] = useState(null);   // AI-built G/B/B held in memory pre-save
  const [aiBusy, setAiBusy] = useState(false);
  // Payment plan — tech picks how the customer pays. Defaults to pay-in-full.
  // When set, the post-approval auto-invoice splits accordingly.
  const [paymentPlan, setPaymentPlan] = useState({ type:'in_full' });

  const subtotal = lines.reduce((s,l)=>s+(Number(l.qty)||0)*(Number(l.price)||0), 0);
  const total = subtotal * (1 + taxRate/100);

  const buildWithAI = async () => {
    if (!lines.some(l => l.name && Number(l.price))) { toast('Add at least one line item first', 'error'); return; }
    setAiBusy(true);
    try {
      const r = await api('/api/estimates/generate-tiers-preview', { method:'POST', body:{ title, lines, taxRate }});
      setPreviewTiers(r.tiers);
      toast('Good / Better / Best built — review before saving');
    } catch (e) { toast(`AI build failed: ${e.message}`, 'error'); }
    finally { setAiBusy(false); }
  };

  const save = async (send) => {
    if (!contact.id) { toast('Pick a contact first', 'error'); return; }
    if (!lines.some(l => l.name && Number(l.price))) { toast('Add at least one line item', 'error'); return; }
    setBusy(true);
    try {
      const created = await api('/api/estimates', { method:'POST', body:{
        contactId: contact.id, contact: `${contact.firstName||''} ${contact.lastName||''}`.trim(),
        title: title || 'Service Estimate', lines, taxRate, paymentTerms, notes,
        status: 'draft',
        validUntil: new Date(Date.now()+30*86400000).toISOString().slice(0,10),
        paymentPlan,
        ...(previewTiers ? { tiers: previewTiers } : {}),
      }});
      let portalUrl = null, sentResp = null;
      if (send) {
        try {
          sentResp = await api(`/api/estimates/${created.id}/send`, { method:'POST' });
          portalUrl = sentResp?.portalUrl || null;
        } catch (e) { toast(`Created but send failed: ${e.message}`, 'warn'); }
      }
      toast(send ? '✓ Estimate sent — open it with the customer below' : '✓ Saved as draft (with Good / Better / Best)');
      setSavedEstimate({ ...created, ...(sentResp||{}), contact, lines, total, portalUrl });
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(false); }
  };

  if (savedEstimate) return <NextStepScreen
    kind="estimate" saved={savedEstimate}
    onConvertToInvoice={() => onSaved({ carry: { contact, lines, lastSavedKind:'estimate', lastSavedId: savedEstimate.id }, nextAction:'invoice' })}
    onGetSignature={() => onSaved({ carry: { contact, lastSavedKind:'estimate', lastSavedId: savedEstimate.id }, nextAction:'signature' })}
    onDone={() => onSaved({ carry: {} })}
  />;

  return <div>
    <Header title="📄 New Estimate" onClose={onClose}/>
    <Field label="Customer"><ContactPicker value={contact.id} onChange={setContact}/></Field>
    <CustomerSummary contact={contact}/>
    <Field label="Title"><Input value={title} onChange={e=>setTitle(e.target.value)} placeholder="e.g. Panel upgrade"/></Field>
    <div style={{ fontSize:12, color:G.textMuted, marginBottom:8, padding:'8px 10px', background:G.purpleDim, borderRadius:6 }}>
      🎯 <strong>Good / Better / Best is auto-generated</strong> from the line items below when you save. Customer picks one when approving.
    </div>
    <LinesEditor lines={lines} setLines={setLines}/>
    <PriceBookBrowser onPick={(p) => setLines([...lines, { name:p.name, qty:1, price:Number(p.price)||0, unit:p.unit||'each' }])}/>
    <BundlesBrowser onPick={(b) => setLines([...lines.filter(l=>l.name), ...(b.lines||[]).map(l=>({ name:l.name, qty:Number(l.qty)||1, price:Number(l.price)||0, unit:l.unit||'each' }))])} toast={toast}/>
    <AiSuggestLines title={title} contact={contact} lines={lines} setLines={setLines} toast={toast}/>
    <ComplementaryItems title={title} lines={lines} setLines={setLines} toast={toast}/>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
      <Field label="Tax Rate %"><Input type="number" step="0.1" value={taxRate} onChange={e=>setTaxRate(Number(e.target.value)||0)}/></Field>
      <Field label="Payment terms" hint="Applied to the invoice when the customer approves.">
        <select value={paymentTerms} onChange={e=>setPaymentTerms(e.target.value)} style={{ ...styles.inp, width:'100%' }}>
          {PAYMENT_TERMS_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
    </div>
    <Field label="Notes for customer"><Textarea value={notes} onChange={e=>setNotes(e.target.value)} style={{ minHeight:60 }}/></Field>
    <PaymentPlanPicker total={total} plan={paymentPlan} setPlan={setPaymentPlan}/>
    <TotalsCard subtotal={subtotal} taxRate={taxRate} total={total}/>
    <MarginGuardrail lines={lines} total={total}/>
    <div style={{ marginBottom:10 }}>
      <Btn sz="sm" v={previewTiers ? 'ghost' : 'primary'} full onClick={buildWithAI} disabled={aiBusy}>
        {aiBusy ? <Spinner sz={14}/> : previewTiers ? 'Rebuild Good / Better / Best with AI' : 'Build Good / Better / Best with AI'}
      </Btn>
    </div>
    {previewTiers && <TierEditor tiers={previewTiers} setTiers={setPreviewTiers}/>}
    <ActionRow busy={busy}
      onDraft={()=>save(false)} draftLabel="Save Draft"
      onSend={()=>save(true)}   sendLabel="Save + Send"/>
  </div>;
}

// Editable Good/Better/Best tiers. Owner/tech can rename, rewrite the pitch,
// add or remove lines, and adjust prices BEFORE the customer sees the
// estimate. Each change updates parent state so the save call ships the
// customized tiers (server skips its own AI gen when tiers are provided).
function TierEditor({ tiers, setTiers }) {
  const updateField = (idx, k, v) => {
    const nt = tiers.map((t, i) => i === idx ? { ...t, [k]: v } : t);
    setTiers(nt);
  };
  const updateLine = (tIdx, lIdx, k, v) => {
    const nt = tiers.map((t, i) => {
      if (i !== tIdx) return t;
      const nl = (t.lines || []).slice();
      nl[lIdx] = { ...nl[lIdx], [k]: (k === 'qty' || k === 'price') ? (Number(v)||0) : v };
      return { ...t, lines: nl };
    });
    setTiers(nt);
  };
  const addLine = (tIdx) => {
    const nt = tiers.map((t, i) => i !== tIdx ? t : { ...t, lines: [...(t.lines||[]), { name:'', qty:1, price:0, unit:'each' }] });
    setTiers(nt);
  };
  const delLine = (tIdx, lIdx) => {
    const nt = tiers.map((t, i) => i !== tIdx ? t : { ...t, lines: (t.lines||[]).filter((_, j) => j !== lIdx) });
    setTiers(nt);
  };
  return <div style={{ ...styles.card, padding:12, marginBottom:12, border:`1px solid ${G.purple}66` }}>
    <div style={{ fontSize:11, fontWeight:700, color:G.purple, textTransform:'uppercase', marginBottom:8, display:'flex', justifyContent:'space-between' }}>
      <span>📝 Customize tiers</span>
      <span style={{ color:G.textMuted, fontWeight:600 }}>Edit anything — saved as-is</span>
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,minmax(0,1fr))', gap:8 }}>
      {tiers.map((t, tIdx) => {
        const total = (t.lines||[]).reduce((s,l)=>s+(Number(l.qty)||1)*(Number(l.price)||0),0);
        return <div key={t.key||tIdx} style={{ padding:10, background:G.input, borderRadius:8, position:'relative', border: tIdx===1 ? `1.5px solid ${G.purple}` : 'none' }}>
          {tIdx === 1 && <div style={{ position:'absolute', top:-8, left:'50%', transform:'translateX(-50%)', background:G.purple, color:'#fff', fontSize:9, padding:'2px 7px', borderRadius:99, fontWeight:700, letterSpacing:'.05em' }}>MOST POPULAR</div>}
          <Input value={t.name||''} onChange={e=>updateField(tIdx,'name',e.target.value)} style={{ fontWeight:700, fontSize:13, padding:'5px 7px' }}/>
          <Input value={t.label||''} onChange={e=>updateField(tIdx,'label',e.target.value)} placeholder="Subtitle" style={{ fontSize:11, marginTop:4, padding:'4px 7px' }}/>
          <Textarea value={t.blurb||''} onChange={e=>updateField(tIdx,'blurb',e.target.value)} placeholder="Why this tier" style={{ fontSize:11, marginTop:4, minHeight:42, padding:'4px 7px' }}/>
          <div style={{ fontWeight:800, fontSize:18, marginTop:6, color:G.green }}>{fmtUsd(total)}</div>
          <div style={{ marginTop:6 }}>
            {(t.lines||[]).map((l, lIdx) => (
              <div key={lIdx} style={{ display:'grid', gridTemplateColumns:'1fr 38px 60px 18px', gap:3, marginBottom:3 }}>
                <Input style={{ fontSize:10, padding:'3px 5px' }} value={l.name||''} onChange={e=>updateLine(tIdx,lIdx,'name',e.target.value)} placeholder="Item"/>
                <Input style={{ fontSize:10, padding:'3px 5px' }} type="number" value={l.qty||0} onChange={e=>updateLine(tIdx,lIdx,'qty',e.target.value)}/>
                <Input style={{ fontSize:10, padding:'3px 5px' }} type="number" value={l.price||0} onChange={e=>updateLine(tIdx,lIdx,'price',e.target.value)}/>
                <Btn sz="xs" v="ghost" onClick={()=>delLine(tIdx,lIdx)}>×</Btn>
              </div>
            ))}
            <Btn sz="xs" v="ghost" onClick={()=>addLine(tIdx)} style={{ marginTop:4, fontSize:10 }}>+ line</Btn>
          </div>
        </div>;
      })}
    </div>
  </div>;
}

// ─── Invoice ────────────────────────────────────────────────────────────────
function InvoiceForm({ toast, carry, onClose, onSaved }) {
  const [contact, setContact] = useState(carry.contact || {});
  const [service, setService] = useState('');
  const [lines, setLines] = useState(carry.lines || [{ name:'', qty:1, price:0 }]);
  const [taxRate, setTaxRate] = useState(7);
  const [busy, setBusy] = useState(false);
  const [savedInvoice, setSavedInvoice] = useState(null);

  const subtotal = lines.reduce((s,l)=>s+(Number(l.qty)||0)*(Number(l.price)||0), 0);
  const total = subtotal * (1 + taxRate/100);

  const save = async (send) => {
    if (!contact.id) { toast('Pick a contact first', 'error'); return; }
    if (!lines.some(l => l.name && Number(l.price))) { toast('Add at least one line item', 'error'); return; }
    setBusy(true);
    try {
      const created = await api('/api/invoices', { method:'POST', body:{
        contactId: contact.id, contact: `${contact.firstName||''} ${contact.lastName||''}`.trim(),
        service, lines, taxRate,
        status: send ? 'sent' : 'draft',
        due: new Date(Date.now()+14*86400000).toISOString().slice(0,10),
      }});
      if (send) { try { await api(`/api/invoices/${created.id}/send`, { method:'POST' }); } catch {} }
      toast(send ? '✓ Invoice sent' : '✓ Saved as draft');
      setSavedInvoice({ ...created, contact, total });
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(false); }
  };

  if (savedInvoice) return <NextStepScreen
    kind="invoice" saved={savedInvoice}
    onCollectPayment={() => onSaved({ carry: { contact, lastSavedKind:'invoice', lastSavedId: savedInvoice.id }, nextAction:'payment' })}
    onGetSignature={() => onSaved({ carry: { contact, lastSavedKind:'invoice', lastSavedId: savedInvoice.id }, nextAction:'signature' })}
    onDone={() => onSaved({ carry: {} })}
  />;

  return <div>
    <Header title={carry.lastSavedKind === 'estimate' ? '💰 Invoice (from estimate)' : '💰 New Invoice'} onClose={onClose}/>
    {carry.lastSavedKind === 'estimate' && <div style={{ ...styles.card, padding:10, marginBottom:14, background:G.purpleDim, color:G.purple, fontSize:12 }}>
      ↻ Pre-filled from the estimate you just saved. Edit anything that should be different.
    </div>}
    <Field label="Customer"><ContactPicker value={contact.id} onChange={setContact}/></Field>
    <CustomerSummary contact={contact}/>
    <Field label="Service"><Input value={service} onChange={e=>setService(e.target.value)} placeholder="e.g. Panel upgrade"/></Field>
    <LinesEditor lines={lines} setLines={setLines}/>
    <PriceBookBrowser onPick={(p) => setLines([...lines, { name:p.name, qty:1, price:Number(p.price)||0, unit:p.unit||'each' }])}/>
    <Field label="Tax Rate %"><Input type="number" step="0.1" value={taxRate} onChange={e=>setTaxRate(Number(e.target.value)||0)}/></Field>
    <TotalsCard subtotal={subtotal} taxRate={taxRate} total={total}/>
    <ActionRow busy={busy}
      onDraft={()=>save(false)} draftLabel="💾 Save Draft"
      onSend={()=>save(true)}   sendLabel="📨 Save + Send"/>
  </div>;
}

// ─── Payment ────────────────────────────────────────────────────────────────
function PaymentForm({ toast, carry, onClose, onSaved }) {
  const [contact, setContact] = useState(carry.contact || {});
  const [invoices, setInvoices] = useState([]);
  const [invoiceId, setInvoiceId] = useState(carry.lastSavedKind === 'invoice' ? carry.lastSavedId : '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!contact.id) { setInvoices([]); return; }
    api(`/api/invoices?contactId=${contact.id}`).then(r => {
      const open = (r||[]).filter(i => i.status !== 'paid');
      setInvoices(open);
      if (!invoiceId && open[0]) setInvoiceId(open[0].id);
    }).catch(()=>{});
  }, [contact.id]);

  const sendPayLink = async () => {
    if (!invoiceId) { toast('Pick an invoice', 'error'); return; }
    setBusy(true);
    try {
      const r = await api(`/api/invoices/${invoiceId}/pay-link`, { method:'POST' });
      const url = r.url || r.paymentLink || '';
      setSent({ url, invoiceId });
      if (url) {
        if (navigator.share) navigator.share({ title: 'Pay your invoice', text: 'Tap to pay:', url }).catch(()=>{});
        else { try { await navigator.clipboard.writeText(url); } catch {} }
      }
      toast('✓ Pay link generated');
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(false); }
  };

  if (sent) return <NextStepScreen
    kind="payment" saved={{ payLinkUrl: sent.url }}
    onDone={() => onSaved({ carry: {} })}
  />;

  return <div>
    <Header title="💳 Collect Payment" onClose={onClose}/>
    <Field label="Customer"><ContactPicker value={contact.id} onChange={setContact}/></Field>
    {contact.id && (invoices.length > 0 ? (
      <Field label={`Open invoices (${invoices.length})`}>
        <Select value={invoiceId} onChange={e=>setInvoiceId(e.target.value)}>
          {invoices.map(i => <option key={i.id} value={i.id}>{i.num} · {fmtUsd(i.total||0)} · {i.status}</option>)}
        </Select>
      </Field>
    ) : <div style={{ ...styles.card, padding:14, marginBottom:14, color:G.textMuted, fontSize:13 }}>
      No open invoices for {contact.firstName}. Create one first.
    </div>)}
    <Btn v="primary" sz="lg" style={{ width:'100%', padding:'14px 0' }} onClick={sendPayLink} disabled={busy || !invoiceId}>
      {busy ? <Spinner sz={16}/> : '💳 Generate Pay Link + Text Customer'}
    </Btn>
  </div>;
}

// ─── Signature ──────────────────────────────────────────────────────────────
// Lets the user pick from preset document templates, drag/drop a PDF/image,
// OR write a one-off agreement inline. Customer signs below the actual
// agreement content, then the signature + doc both get saved to their record.
//
// Templates support {{customerName}}, {{customerAddress}}, {{today}}, etc.
// — auto-filled with the picked contact + the account's business data.
function SignatureForm({ toast, carry, onClose, onSaved }) {
  const [contact, setContact] = useState(carry.contact || {});
  const [account, setAccount] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [docTitle, setDocTitle] = useState(carry.lastSavedKind === 'estimate' ? 'Estimate Approval' : 'Service Agreement');
  const [docBody, setDocBody] = useState('');
  const [rawTemplateBody, setRawTemplateBody] = useState(''); // pre-interpolation copy
  const [uploadedFile, setUploadedFile] = useState(null); // { url, kind, name }
  const [dragOver, setDragOver] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Load templates + business profile on mount
  useEffect(() => {
    api('/api/document-templates').then(list => {
      setTemplates(list || []);
      const want = carry.lastSavedKind === 'estimate' ? /estimate|service/i : /service|agreement/i;
      const pick = (list||[]).find(t => want.test(t.name)) || (list||[])[0];
      if (pick) { setTemplateId(pick.id); setDocTitle(pick.name); setRawTemplateBody(pick.body || ''); }
    }).catch(()=>{});
    api('/api/auth/user').then(u => setAccount(u.account || null)).catch(()=>{});
  }, []);

  // Build the variable substitution map. Recomputed whenever contact or account changes.
  const variables = React.useMemo(() => {
    const today = new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' });
    const customerName = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
    return {
      customerName: customerName || '________________',
      customerFirstName: contact.firstName || '',
      customerLastName: contact.lastName || '',
      customerPhone: contact.phone || '',
      customerEmail: contact.email || '',
      customerAddress: contact.address || '',
      customerCity: contact.city || '',
      customerState: contact.state || '',
      customerZip: contact.zip || '',
      businessName: account?.name || 'Contractor',
      businessPhone: account?.phone || '',
      businessLicense: account?.licenseNumber || '',
      today,
      techName: '',
      jobAddress: contact.address || '',
    };
  }, [contact, account]);

  // Re-interpolate whenever variables OR template body change. Keep `rawTemplateBody`
  // unmolested so picking a new contact re-fills cleanly.
  useEffect(() => {
    if (!rawTemplateBody) return;
    const filled = rawTemplateBody.replace(/\{\{(\w+)\}\}/g, (_, k) => variables[k] != null ? variables[k] : `{{${k}}}`);
    setDocBody(filled);
  }, [rawTemplateBody, variables]);

  const onPickTemplate = (id) => {
    const t = templates.find(x => x.id === id);
    if (!t) { setTemplateId(''); return; }
    setTemplateId(id);
    setDocTitle(t.name);
    setRawTemplateBody(t.body || '');
    setUploadedFile(t.fileUrl ? { url: t.fileUrl, kind: t.fileKind, name: t.name } : null);
  };

  // File upload: turn into base64, POST to /api/uploads, save returned URL
  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast('File too large (8MB max)', 'error'); return; }
    setBusy(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await api('/api/uploads', { method:'POST', body:{ name: file.name, mime: file.type, base64: dataUrl }});
      setUploadedFile({ url: r.url, kind: file.type, name: file.name });
      setDocTitle(file.name.replace(/\.[^.]+$/, '') || 'Uploaded Agreement');
      setDocBody('');
      toast(`✓ ${file.name} attached`);
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0]; if (f) handleFile(f);
  };

  // Compute the canvas coords from a mouse/touch event, accounting for the
  // canvas's internal resolution vs CSS-rendered size. Without this, touch
  // points drift on retina displays where canvas.width != getBoundingClientRect.width.
  const eventCoords = (e) => {
    const c = canvasRef.current; if (!c) return null;
    const r = c.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    return {
      x: (clientX - r.left) * (c.width / r.width),
      y: (clientY - r.top)  * (c.height / r.height),
    };
  };
  const startDraw = (e) => {
    const c = canvasRef.current; if (!c) return;
    setDrawing(true);
    const ctx = c.getContext('2d');
    ctx.beginPath();
    const p = eventCoords(e); if (!p) return;
    ctx.moveTo(p.x, p.y);
    e.preventDefault();
  };
  const moveDraw = (e) => {
    if (!drawing) return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    // Pen-style ink: opaque black, slightly thicker line, butt-rounded.
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0a0a0a';
    const p = eventCoords(e); if (!p) return;
    ctx.lineTo(p.x, p.y); ctx.stroke();
    e.preventDefault();
  };
  const endDraw = () => setDrawing(false);
  const clearSig = () => { const c = canvasRef.current; if (c) c.getContext('2d').clearRect(0,0,c.width,c.height); };

  const submit = async () => {
    if (!contact.id) { toast('Pick a customer', 'error'); return; }
    if (!docBody && !uploadedFile) { toast('Pick a template or attach a document first', 'error'); return; }
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    const data = ctx.getImageData(0,0,c.width,c.height).data;
    const hasInk = Array.from(data).some((v,i) => i%4===3 && v > 0);
    if (!hasInk) { toast('Have the customer sign first', 'error'); return; }
    const signatureDataUrl = c.toDataURL('image/png');
    setBusy(true);
    try {
      await api('/api/documents', { method:'POST', body:{
        contactId: contact.id, title: docTitle, body: docBody,
        attachmentUrl: uploadedFile?.url || null,
        attachmentKind: uploadedFile?.kind || null,
        templateId: templateId || null,
        signatureImage: signatureDataUrl, signedAt: new Date().toISOString(),
        relatedEstimateId: carry.lastSavedKind === 'estimate' ? carry.lastSavedId : null,
        relatedInvoiceId:  carry.lastSavedKind === 'invoice' ? carry.lastSavedId : null,
      }});
      // Optionally save current body as a new template for next time
      if (saveAsTemplate && docBody && !templateId) {
        try {
          await api('/api/document-templates', { method:'POST', body:{ name: docTitle, body: docBody }});
        } catch {}
      }
      toast(`✓ Signed agreement saved to ${contact.firstName}'s record`);
      setDone(true);
    } catch(e) { toast(e.message, 'error'); } finally { setBusy(false); }
  };

  if (done) return <NextStepScreen
    kind="signature"
    saved={{ contact }}
    onCollectPayment={carry.lastSavedKind === 'invoice' ? () => onSaved({ carry: { contact, lastSavedKind:'invoice', lastSavedId: carry.lastSavedId }, nextAction:'payment' }) : null}
    onCreateInvoice={carry.lastSavedKind === 'estimate' ? () => onSaved({ carry: { contact, lines: carry.lines, lastSavedKind:'estimate', lastSavedId: carry.lastSavedId }, nextAction:'invoice' }) : null}
    onDone={() => onSaved({ carry: {} })}
  />;

  const isPdf = uploadedFile && /pdf/i.test(uploadedFile.kind || '');
  const isImage = uploadedFile && /image/i.test(uploadedFile.kind || '');

  return <div>
    <Header title="✍️ Get Signature" onClose={onClose}/>
    <Field label="Customer"><ContactPicker value={contact.id} onChange={setContact}/></Field>

    {/* Document source — preset template OR uploaded file */}
    <Field label="Document">
      <Select value={templateId} onChange={e=>onPickTemplate(e.target.value)}>
        <option value="">— Pick a template —</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (default)' : ''}</option>)}
      </Select>
    </Field>

    {/* Drag-and-drop upload zone */}
    <div
      onDragOver={(e)=>{ e.preventDefault(); setDragOver(true); }}
      onDragLeave={()=>setDragOver(false)}
      onDrop={onDrop}
      onClick={async ()=>{
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,image/*,.doc,.docx';
        input.onchange = (e) => handleFile(e.target.files?.[0]);
        input.click();
      }}
      style={{
        ...styles.card, padding:14, marginBottom:14,
        border: `2px dashed ${dragOver ? G.purple : G.cardBorder}`,
        background: dragOver ? G.purpleDim : G.input,
        textAlign:'center', cursor:'pointer',
      }}
    >
      {uploadedFile ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
          <span style={{ fontSize:24 }}>{isPdf ? '📄' : isImage ? '🖼️' : '📎'}</span>
          <strong>{uploadedFile.name}</strong>
          <Btn sz="xs" v="ghost" onClick={(e)=>{ e.stopPropagation(); setUploadedFile(null); }}>✕</Btn>
        </div>
      ) : (
        <div style={{ color:G.textMuted, fontSize:13 }}>
          📁 <strong>Drag a PDF here</strong> or tap to browse — overrides the template text
        </div>
      )}
    </div>

    {/* Show the actual agreement content (the part the customer is signing) */}
    {uploadedFile ? (
      <div style={{ ...styles.card, padding:0, marginBottom:14, overflow:'hidden' }}>
        {isPdf && <iframe src={uploadedFile.url} style={{ width:'100%', height:260, border:'none', background:'#fff' }} title="Agreement preview"/>}
        {isImage && <img src={uploadedFile.url} alt="Agreement" style={{ width:'100%', maxHeight:260, objectFit:'contain', background:'#fff' }}/>}
        {!isPdf && !isImage && <div style={{ padding:14, fontSize:12, color:G.textMuted }}>📎 Attached: <a href={uploadedFile.url} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>{uploadedFile.name}</a></div>}
      </div>
    ) : (
      <>
        <Field label="Document Title"><Input value={docTitle} onChange={e=>setDocTitle(e.target.value)}/></Field>
        <Field label="Agreement Text (what the customer is agreeing to)">
          <Textarea value={docBody} onChange={e=>{ setDocBody(e.target.value); setTemplateId(''); }} style={{ minHeight:200, fontFamily:'system-ui, sans-serif', fontSize:13, lineHeight:1.5 }}/>
        </Field>
        {!templateId && docBody && (
          <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, fontSize:12, color:G.textMuted, cursor:'pointer' }}>
            <input type="checkbox" checked={saveAsTemplate} onChange={e=>setSaveAsTemplate(e.target.checked)}/>
            <span>💾 Save "{docTitle}" as a template for next time</span>
          </label>
        )}
      </>
    )}

    <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Customer signs below</div>
    {/* High-resolution backing buffer (800×320) renders pen-quality ink even
        when the canvas is CSS-scaled to phone screen width. */}
    <canvas ref={canvasRef} width={800} height={320}
      style={{ width:'100%', height:200, background:'#fff', borderRadius:8, border:`2px dashed ${G.cardBorder}`, touchAction:'none', marginBottom:8, display:'block' }}
      onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
      onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}/>
    <div style={{ display:'flex', gap:8, marginBottom:14 }}>
      <Btn sz="xs" v="ghost" onClick={clearSig} style={{ flex:1 }}>↺ Clear Signature</Btn>
    </div>
    <Btn v="primary" sz="lg" style={{ width:'100%', padding:'14px 0' }} onClick={submit} disabled={busy}>
      {busy ? <Spinner sz={16}/> : '✓ Save Signed Agreement'}
    </Btn>
  </div>;
}

// ─── Shared sub-components ─────────────────────────────────────────────────

// Shows the selected contact's address/phone/email so the owner can verify
// the data going on the customer-facing estimate/invoice.
function CustomerSummary({ contact }) {
  if (!contact || !contact.id) return null;
  const parts = [contact.address, contact.city, contact.state, contact.zip].filter(Boolean).join(', ');
  return <div style={{ ...styles.card, padding:'10px 14px', marginBottom:14, background:G.input, fontSize:12 }}>
    <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>📋 Bill To</div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:6, color:G.textMuted }}>
      <div><strong style={{ color:G.text }}>{[contact.firstName, contact.lastName].filter(Boolean).join(' ')}</strong></div>
      {contact.phone && <div>📞 {contact.phone}</div>}
      {contact.email && <div>✉️ {contact.email}</div>}
      {parts && <div>📍 {parts}</div>}
    </div>
  </div>;
}

// Bundles — owner-saved multi-line packages. Tech taps a bundle → all its
// lines drop into the estimate at once. Massive speed win for common jobs.
function BundlesBrowser({ onPick, toast }) {
  const [bundles, setBundles] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api('/api/bundles').then(setBundles).catch(()=>{}); }, []);
  if (bundles.length === 0 && !open) return null;
  return <div style={{ marginBottom:14 }}>
    <Btn sz="sm" v="cyan" onClick={()=>setOpen(!open)}>📦 Bundles ({bundles.length})</Btn>
    {open && <div style={{ marginTop:8, padding:10, background:G.input, borderRadius:8 }}>
      {bundles.length === 0 && <div style={{ color:G.textMuted, fontSize:12, padding:8, textAlign:'center' }}>
        No bundles yet. Owner can create them in the Price Book screen.
      </div>}
      {bundles.map(b => {
        const total = (b.lines||[]).reduce((s,l)=>s+(Number(l.qty)||1)*(Number(l.price)||0), 0);
        return <div key={b.id} onClick={()=>{ onPick(b); setOpen(false); toast?.(`✓ ${b.name} added — ${(b.lines||[]).length} lines`); }}
          style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', cursor:'pointer', borderRadius:6, borderBottom:`1px solid ${G.cardBorder}` }}
          onMouseEnter={e=>e.currentTarget.style.background=G.purpleDim}
          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:700 }}>{b.name}</div>
            {b.description && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{b.description}</div>}
            <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{(b.lines||[]).length} line{(b.lines||[]).length===1?'':'s'}</div>
          </div>
          <div style={{ fontWeight:700, color:G.green, fontSize:14, marginLeft:10 }}>${total.toFixed(0)}</div>
        </div>;
      })}
    </div>}
  </div>;
}

// Live margin guardrail. Pulls cost data from the account's price book and
// shows the tech how their estimate compares to target margin. Green ≥30%,
// yellow 15-29%, red <15%. Visible only to internal users (tech/owner) —
// never rendered on the customer-facing portal.
function MarginGuardrail({ lines, total }) {
  const [costMap, setCostMap] = useState({});
  useEffect(() => {
    api('/api/pricebook').then(items => {
      const m = {};
      for (const it of (Array.isArray(items) ? items : [])) {
        if (it.name) m[it.name.toLowerCase()] = Number(it.cost) || 0;
      }
      setCostMap(m);
    }).catch(()=>{});
  }, []);
  const costSum = lines.reduce((s, l) => {
    if (!l.name) return s;
    const c = costMap[l.name.toLowerCase()] || 0;
    return s + (Number(l.qty)||1) * c;
  }, 0);
  if (total <= 0) return null;
  const margin = total > 0 ? ((total - costSum) / total) * 100 : 0;
  const color = margin >= 30 ? G.green : margin >= 15 ? G.yellow : G.red;
  const label = margin >= 30 ? 'Healthy' : margin >= 15 ? 'Tight — watch it' : 'Below target';
  return <div style={{ ...styles.card, padding:12, marginBottom:14, background:color+'14', borderColor:color+'66' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <div>
        <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Live margin</div>
        <div style={{ fontSize:22, fontWeight:800, color, marginTop:2 }}>{margin.toFixed(0)}% <span style={{ fontSize:12, fontWeight:600, color:G.textMuted }}>· {label}</span></div>
      </div>
      <div style={{ textAlign:'right', fontSize:11, color:G.textMuted }}>
        <div>Revenue: <strong style={{ color:G.text }}>${total.toFixed(0)}</strong></div>
        <div>Est. cost: <strong style={{ color:G.text }}>${costSum.toFixed(0)}</strong></div>
        <div>Profit: <strong style={{ color }}>${(total - costSum).toFixed(0)}</strong></div>
      </div>
    </div>
    {margin < 15 && <div style={{ fontSize:11, color:G.red, marginTop:6 }}>
      ⚠ Margin under 15% — confirm pricing or upgrade tier before sending.
    </div>}
  </div>;
}

// Expandable Price Book browser — category-grouped (ServiceTitan-style) so
// techs can drill down "HVAC → Repairs → Capacitors" instead of scrolling a
// flat 200-item list. Type to filter inline; when filtered we show flat
// results sorted by relevance. Tap an item to add it.
function PriceBookBrowser({ onPick }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [expandedTops, setExpandedTops] = useState({});
  const [expandedSubs, setExpandedSubs] = useState({});
  useEffect(() => { api('/api/pricebook').then(r => setItems(Array.isArray(r) ? r : [])).catch(()=>{}); }, []);
  const live = items.filter(p => Number(p.price) > 0);

  const tree = (() => {
    const term = q.trim().toLowerCase();
    const tokens = term.split(/\s+/).filter(Boolean);
    const matched = live.filter(p => {
      if (!term) return true;
      const hay = `${p.name||''} ${p.sku||''} ${p.category||''} ${p.description||''}`.toLowerCase();
      return tokens.every(t => hay.includes(t));
    });
    matched.sort((a,b) => String(a.name||'').localeCompare(String(b.name||'')));
    const t = {};
    for (const p of matched) {
      const parts = (p.category || 'Uncategorized').split('/').map(s=>s.trim()).filter(Boolean);
      const top = parts[0] || 'Uncategorized';
      const sub = parts[1] || 'General';
      t[top] = t[top] || {};
      t[top][sub] = t[top][sub] || [];
      t[top][sub].push(p);
    }
    return { tree: t, totalMatched: matched.length };
  })();

  const toggleTop = (top) => setExpandedTops(s => ({ ...s, [top]: !s[top] }));
  const toggleSub = (key) => setExpandedSubs(s => ({ ...s, [key]: !s[key] }));
  // When searching, auto-expand every group so hits are visible.
  const isSearching = !!q.trim();

  return <div style={{ marginBottom:14 }}>
    <Btn sz="sm" v="cyan" onClick={()=>setOpen(!open)}>📚 From Price Book ({live.length})</Btn>
    {open && <div style={{ marginTop:8, padding:10, background:G.input, borderRadius:8, maxHeight:420, overflow:'auto' }}>
      <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search price book by name, SKU, or category…" style={{ marginBottom:8 }} autoFocus/>
      <div style={{ fontSize:10, fontWeight:700, color:G.textDim, textTransform:'uppercase', padding:'4px 4px 8px', display:'flex', justifyContent:'space-between' }}>
        <span>{isSearching ? `${tree.totalMatched} match${tree.totalMatched===1?'':'es'}` : `Browse ${live.length} items`}</span>
      </div>
      {live.length === 0 && <div style={{ color:G.textMuted, fontSize:12, padding:8, textAlign:'center' }}>
        No price book items yet — add some in the Price Book screen.
      </div>}
      {Object.keys(tree.tree).sort().map(top => {
        const subs = tree.tree[top];
        const topCount = Object.values(subs).reduce((s, arr) => s + arr.length, 0);
        const topOpen = isSearching || expandedTops[top];
        return <div key={top} style={{ marginBottom:4 }}>
          <div onClick={()=>toggleTop(top)} style={{ display:'flex', justifyContent:'space-between', padding:'8px 10px', cursor:'pointer', background:G.purpleDim+'33', borderRadius:6, fontSize:12, fontWeight:800, color:G.purple, textTransform:'uppercase', letterSpacing:'.05em' }}>
            <span>{topOpen?'▾':'▸'} {top}</span>
            <span style={{ color:G.textMuted, fontWeight:600 }}>{topCount}</span>
          </div>
          {topOpen && Object.keys(subs).sort().map(sub => {
            const key = `${top}/${sub}`;
            const subOpen = isSearching || expandedSubs[key] || Object.keys(subs).length === 1;
            return <div key={sub} style={{ marginLeft:8 }}>
              {Object.keys(subs).length > 1 && <div onClick={()=>toggleSub(key)} style={{ padding:'4px 10px', cursor:'pointer', fontSize:11, color:G.textMuted, fontWeight:700 }}>
                {subOpen?'▾':'▸'} {sub} <span style={{ fontWeight:500 }}>({subs[sub].length})</span>
              </div>}
              {subOpen && subs[sub].map(p => (
                <div key={p.id} onClick={()=>{ onPick(p); }} style={{ padding:'10px 12px 10px 18px', cursor:'pointer', borderRadius:6, display:'flex', justifyContent:'space-between', alignItems:'center' }}
                  onMouseEnter={e=>e.currentTarget.style.background=G.purpleDim}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize:11, color:G.textMuted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {p.sku ? `${p.sku} · ` : ''}{p.unit ? `per ${p.unit}` : ''}{p.description ? ` · ${p.description.slice(0,40)}` : ''}
                    </div>
                  </div>
                  <div style={{ fontWeight:700, color:G.green, fontSize:14, marginLeft:10, whiteSpace:'nowrap' }}>${Number(p.price||0).toFixed(2)}</div>
                </div>
              ))}
            </div>;
          })}
        </div>;
      })}
    </div>}
  </div>;
}

// Complementary-items suggester. Watches the current line list and asks the
// AI which items from the price book are typically added to complete this
// kind of job (capacitor with a compressor, wax ring with a toilet, etc.).
// Tap "Add" on a suggestion to drop it into the estimate.
// Tech-friendly payment plan selector. Four options:
//   - in_full           : single invoice, balance due on completion
//   - deposit_balance   : % or fixed deposit + balance on completion
//   - milestones        : 3 milestones (default 25/50/25, customer-editable)
//   - financing         : route to financing partners (existing flow)
// When the customer approves, the auto-invoice flow creates segment invoices
// according to this plan. Each invoice gets its own Stripe Payment Link.
export function PaymentPlanPicker({ total, plan, setPlan }) {
  const t = plan?.type || 'in_full';
  const set = (next) => setPlan(next);
  // Snap deposit to a sensible default when switching to deposit/milestone modes.
  const switchTo = (type) => {
    if (type === 'in_full') return set({ type:'in_full' });
    if (type === 'deposit_balance') return set({ type:'deposit_balance', deposit:{ kind:'pct', value:50 } });
    if (type === 'milestones') return set({ type:'milestones', milestones:[
      { label:'Deposit (at signing)',     pct:25, trigger:'signed' },
      { label:'Materials drop',           pct:25, trigger:'on_site' },
      { label:'Rough-in / mid-job',       pct:25, trigger:'midway' },
      { label:'Final on completion',      pct:25, trigger:'completed' },
    ]});
    if (type === 'financing') return set({ type:'financing' });
  };
  const updateDeposit = (k, v) => set({ ...plan, deposit:{ ...(plan.deposit||{}), [k]: k==='kind'? v : (Number(v)||0) }});
  const updateMilestone = (i, k, v) => {
    const ms = (plan.milestones||[]).slice();
    ms[i] = { ...ms[i], [k]: k==='pct' ? (Number(v)||0) : v };
    set({ ...plan, milestones: ms });
  };
  const depositDollar = (() => {
    if (t !== 'deposit_balance' || !plan.deposit) return 0;
    if (plan.deposit.kind === 'pct') return total * (plan.deposit.value/100);
    return Math.min(Number(plan.deposit.value)||0, total);
  })();
  const msTotal = (plan.milestones||[]).reduce((s,m)=>s+(Number(m.pct)||0), 0);
  return <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor: G.purple+'66' }}>
    <div style={{ fontSize:11, fontWeight:800, color:G.purple, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>💳 Payment Plan</div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:6, marginBottom:10 }}>
      {[
        { id:'in_full',         label:'Pay in full',    sub:'Single invoice' },
        { id:'deposit_balance', label:'Deposit + balance', sub:'Common for installs' },
        { id:'milestones',      label:'Milestones',     sub:'Bigger projects' },
        { id:'financing',       label:'Financing',      sub:'Monthly payments' },
      ].map(o => (
        <button key={o.id} onClick={()=>switchTo(o.id)} style={{
          padding:'10px 6px', borderRadius:8, fontSize:11, fontWeight:700, cursor:'pointer',
          background: t===o.id ? G.purple : 'transparent',
          color: t===o.id ? '#fff' : G.text,
          border: `1px solid ${t===o.id ? G.purple : G.cardBorder}`,
        }}>
          {o.label}
          <div style={{ fontSize:9, opacity:.85, fontWeight:500, marginTop:2 }}>{o.sub}</div>
        </button>
      ))}
    </div>

    {t === 'in_full' && <div style={{ fontSize:12, color:G.textMuted }}>One invoice, full amount due {`($${total.toFixed(0)})`}. Customer pays via Stripe (Apple Pay, Google Pay, Klarna, Affirm, card, ACH).</div>}

    {t === 'deposit_balance' && <div>
      <div style={{ display:'grid', gridTemplateColumns:'90px 1fr 1fr', gap:8, alignItems:'center' }}>
        <select value={plan.deposit?.kind||'pct'} onChange={e=>updateDeposit('kind', e.target.value)} style={{ ...styles.inp, padding:'6px 8px', fontSize:13 }}>
          <option value="pct">% deposit</option>
          <option value="fixed">$ deposit</option>
        </select>
        <Input type="number" value={plan.deposit?.value ?? 50} onChange={e=>updateDeposit('value', e.target.value)} placeholder={plan.deposit?.kind==='pct'?'50':'500'}/>
        <div style={{ fontSize:13, color:G.green, fontWeight:700, textAlign:'right' }}>
          ${depositDollar.toFixed(0)} now · ${Math.max(0, total - depositDollar).toFixed(0)} on completion
        </div>
      </div>
    </div>}

    {t === 'milestones' && <div>
      {(plan.milestones||[]).map((m, i) => (
        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 70px 130px 90px', gap:6, marginBottom:6, alignItems:'center' }}>
          <Input value={m.label||''} onChange={e=>updateMilestone(i,'label',e.target.value)} placeholder="Milestone"/>
          <Input type="number" value={m.pct||0} onChange={e=>updateMilestone(i,'pct',e.target.value)} placeholder="%"/>
          <select value={m.trigger||'signed'} onChange={e=>updateMilestone(i,'trigger',e.target.value)} style={{ ...styles.inp, padding:'6px 8px', fontSize:12 }}>
            <option value="signed">When signed</option>
            <option value="on_site">Tech on site</option>
            <option value="midway">Midway</option>
            <option value="completed">Completed</option>
            <option value="manual">Manual</option>
          </select>
          <div style={{ fontSize:13, color:G.green, fontWeight:700, textAlign:'right' }}>
            ${(total * (Number(m.pct)||0)/100).toFixed(0)}
          </div>
        </div>
      ))}
      <div style={{ fontSize:11, color: msTotal === 100 ? G.green : G.red, fontWeight:700, marginTop:4 }}>
        Total: {msTotal}% {msTotal !== 100 && '— must equal 100%'}
      </div>
    </div>}

    {t === 'financing' && <div style={{ fontSize:12, color:G.textMuted }}>
      Customer applies through Wisetack / GoodLeap on the approval page — TradeOps gets paid in full, customer pays the lender monthly. 30-sec soft pull, no impact to credit.
    </div>}
  </div>;
}

export function ComplementaryItems({ title, lines, setLines, toast }) {
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const nonEmpty = lines.filter(l => l && l.name);
  const lineKey = nonEmpty.map(l => l.name).join('|');
  const run = async () => {
    if (nonEmpty.length === 0) { toast?.('Add at least one line first', 'warn'); return; }
    setBusy(true);
    try {
      const r = await api('/api/estimates/complementary-items', { method:'POST', body:{ currentLines: nonEmpty, jobTitle: title }});
      const fresh = (r.lines || []).filter(s => !dismissed.has(s.name));
      setSuggestions(fresh);
      if (fresh.length === 0) toast?.('No complementary items found — your estimate looks complete', 'info');
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setBusy(false); }
  };
  const add = (s) => {
    setLines([...lines.filter(l => l.name), { name: s.name, qty: s.qty || 1, price: Number(s.price)||0, unit: s.unit || 'each' }]);
    setSuggestions(suggestions.filter(x => x.name !== s.name));
  };
  const dismiss = (s) => {
    setDismissed(new Set([...dismissed, s.name]));
    setSuggestions(suggestions.filter(x => x.name !== s.name));
  };
  return <div style={{ marginBottom:14 }}>
    <Btn sz="sm" v="purple" onClick={run} disabled={busy || nonEmpty.length === 0}>
      {busy ? <Spinner sz={14}/> : '🧩 Complete the Job'}
    </Btn>
    <span style={{ marginLeft:8, fontSize:11, color:G.textMuted }}>Suggests items from your price book to finish this job (capacitors, wax rings, etc).</span>
    {suggestions.length > 0 && <div style={{ ...styles.card, padding:10, marginTop:8, background:G.purpleDim+'22', borderColor:G.purple+'66' }}>
      <div style={{ fontSize:11, fontWeight:700, color:G.purple, textTransform:'uppercase', marginBottom:8 }}>
        🧩 Commonly added to finish jobs like this
      </div>
      {suggestions.map((s, i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 4px', borderTop: i===0 ? 'none' : `1px solid ${G.cardBorder}` }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600 }}>{s.name} <span style={{ color:G.green, fontWeight:700 }}>${Number(s.price||0).toFixed(2)}</span></div>
            {s.reason && <div style={{ fontSize:11, color:G.textMuted, fontStyle:'italic' }}>{s.reason}</div>}
          </div>
          <div style={{ display:'flex', gap:6, marginLeft:10 }}>
            <Btn sz="xs" v="primary" onClick={()=>add(s)}>+ Add</Btn>
            <Btn sz="xs" v="ghost" onClick={()=>dismiss(s)}>Skip</Btn>
          </div>
        </div>
      ))}
    </div>}
  </div>;
}

// AI Suggest button — given the estimate title + customer context, asks AI
// to suggest line items from the account's price book. Adds suggested items
// directly to the line list.
function AiSuggestLines({ title, contact, lines, setLines, toast }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!title.trim()) { toast?.('Enter a title first so AI knows what to suggest for', 'error'); return; }
    setBusy(true);
    try {
      const r = await api('/api/estimates/suggest-lines', { method:'POST', body:{
        title,
        customerNotes: contact?.notes || '',
        existingLineCount: lines.filter(l => l.name).length,
      }});
      const suggested = Array.isArray(r?.lines) ? r.lines : [];
      if (suggested.length === 0) { toast?.('AI had no suggestions for that title — try being more specific', 'warn'); return; }
      // Append, don't replace
      const existing = lines.filter(l => l.name);
      setLines([...existing, ...suggested.map(s => ({ name: s.name, qty: s.qty || 1, price: Number(s.price)||0, unit: s.unit || 'each' }))]);
      toast?.(`✨ Added ${suggested.length} AI-suggested line${suggested.length===1?'':'s'} — review and adjust`);
    } catch(e) { toast?.(e.message, 'error'); } finally { setBusy(false); }
  };
  return <div style={{ marginBottom:14 }}>
    <Btn sz="sm" v="purple" onClick={run} disabled={busy}>{busy ? <Spinner sz={14}/> : '✨ AI Suggest from Price Book'}</Btn>
    <span style={{ marginLeft:8, fontSize:11, color:G.textMuted }}>Pulls relevant items from your Price Book based on the title.</span>
  </div>;
}

function Header({ title, onClose }) {
  return <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
    <h2 style={{ margin:0, fontSize:18 }}>{title}</h2>
    <Btn sz="sm" v="ghost" onClick={onClose}>✕ Cancel</Btn>
  </div>;
}

function LinesEditor({ lines, setLines }) {
  return <div style={{ ...styles.card, padding:12, marginBottom:14 }}>
    <div style={{ fontSize:11, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:8 }}>Line items <span style={{ fontWeight:500, textTransform:'none', color:G.textDim }}>(type to search Price Book)</span></div>
    {lines.map((l,i) => (
      <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 56px 80px 28px', gap:6, marginBottom:6 }}>
        <PriceBookPicker
          value={l.name}
          onPick={(picked) => {
            const nl = [...lines];
            // Picked from price book → fill name + price + qty + unit. Manual type → just update name.
            nl[i] = picked.price != null
              ? { ...l, name: picked.name, price: picked.price, qty: picked.qty || l.qty || 1, unit: picked.unit || l.unit }
              : { ...l, name: picked.name };
            setLines(nl);
          }}
        />
        <Input type="number" value={l.qty} onChange={e=>{ const nl=[...lines]; nl[i]={...l,qty:Number(e.target.value)||0}; setLines(nl); }} placeholder="Qty"/>
        <Input type="number" value={l.price} onChange={e=>{ const nl=[...lines]; nl[i]={...l,price:Number(e.target.value)||0}; setLines(nl); }} placeholder="Price"/>
        <Btn sz="xs" v="ghost" onClick={()=>{ if (lines.length===1) return; setLines(lines.filter((_,j)=>j!==i)); }}>✕</Btn>
      </div>
    ))}
    <Btn sz="xs" v="ghost" onClick={()=>setLines([...lines, { name:'', qty:1, price:0 }])}>+ Add line</Btn>
  </div>;
}

function TotalsCard({ subtotal, taxRate, total }) {
  return <div style={{ ...styles.card, padding:14, marginBottom:14, background:G.input }}>
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:G.textMuted }}><span>Subtotal</span><span>{fmtUsd(subtotal)}</span></div>
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:G.textMuted, marginTop:4 }}><span>Tax ({taxRate}%)</span><span>{fmtUsd(subtotal*taxRate/100)}</span></div>
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:18, fontWeight:800, marginTop:8, paddingTop:8, borderTop:`1px solid ${G.cardBorder}` }}>
      <span>Total</span><span style={{ color:G.green }}>{fmtUsd(total)}</span>
    </div>
  </div>;
}

function ActionRow({ busy, onDraft, draftLabel, onSend, sendLabel }) {
  return <div style={{ display:'flex', gap:8 }}>
    <Btn sz="sm" style={{ flex:1 }} onClick={onDraft} disabled={busy}>{busy?<Spinner sz={14}/>:draftLabel}</Btn>
    <Btn sz="sm" v="primary" style={{ flex:1 }} onClick={onSend} disabled={busy}>{busy?<Spinner sz={14}/>:sendLabel}</Btn>
  </div>;
}

// Shown after a successful save. The chain choices are the "seamless" part —
// one tap takes you to the next likely step.
function NextStepScreen({ kind, saved, onConvertToInvoice, onCreateInvoice, onCollectPayment, onGetSignature, onDone }) {
  // ESTIMATE: special flow — tech wants to walk the customer through the
  // Good/Better/Best options right there. Embed the customer-facing portal
  // page so the tech can show their phone to the customer, tap the tier the
  // customer chooses, and convert to invoice on the spot.
  if (kind === 'estimate' && saved?.portalUrl) {
    return <EstimateInPersonFlow saved={saved} onConvertToInvoice={onConvertToInvoice} onGetSignature={onGetSignature} onDone={onDone}/>;
  }

  const messages = {
    estimate: '✓ Estimate saved!',
    invoice:  '✓ Invoice saved!',
    payment:  '✓ Pay link generated!',
    signature:'✓ Signature captured!',
  };
  return <div style={{ textAlign:'center', padding:'10px 0' }}>
    <div style={{ fontSize:48, marginBottom:6 }}>🎉</div>
    <div style={{ fontSize:20, fontWeight:800, marginBottom:18 }}>{messages[kind]}</div>
    {kind === 'payment' && saved?.payLinkUrl && (
      <div style={{ ...styles.card, padding:14, marginBottom:14, background:G.input, fontSize:12, wordBreak:'break-all' }}>
        <div style={{ color:G.textMuted, marginBottom:4 }}>Pay link (already copied):</div>
        <a href={saved.payLinkUrl} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>{saved.payLinkUrl}</a>
      </div>
    )}
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>What's next?</div>
    <div style={{ display:'grid', gap:8, paddingBottom:'120px' }}>
      {onConvertToInvoice && <Btn v="primary" sz="lg" onClick={onConvertToInvoice}>💰 Convert to Invoice</Btn>}
      {onCreateInvoice    && <Btn v="primary" sz="lg" onClick={onCreateInvoice}>💰 Create Invoice for this customer</Btn>}
      {onGetSignature     && <Btn sz="lg" onClick={onGetSignature}>✍️ Get a Signature</Btn>}
      {onCollectPayment   && <Btn v="primary" sz="lg" onClick={onCollectPayment}>💳 Collect Payment Now</Btn>}
      <Btn v="ghost" sz="sm" onClick={onDone}>← Back to start</Btn>
    </div>
  </div>;
}

// In-person estimate review screen. Embeds the customer-facing portal page so
// the tech can show their phone to the customer. Tap a tier when the customer
// picks one — that selection is saved server-side. Then "Customer approved →
// Convert to Invoice" creates the invoice from the selected tier's lines.
function EstimateInPersonFlow({ saved, onConvertToInvoice, onGetSignature, onDone }) {
  const [selectedTier, setSelectedTier] = useState(saved.selectedTier || null);
  const [savingTier, setSavingTier] = useState(false);
  const tiers = Array.isArray(saved.tiers) ? saved.tiers : [];
  const pickTier = async (key) => {
    setSavingTier(true);
    try {
      await api(`/api/estimates/${saved.id}`, { method: 'PATCH', body: { selectedTier: key }});
      setSelectedTier(key);
    } catch (e) { alert('Could not save tier choice: ' + e.message); }
    finally { setSavingTier(false); }
  };
  const copyLink = () => {
    try { navigator.clipboard.writeText(saved.portalUrl); }
    catch { window.prompt('Customer link:', saved.portalUrl); }
  };
  // Compute totals from the saved tiers so the tech can show the customer
  // the headline numbers inline — no iframe needed. (Iframes also blocked
  // Stripe from loading because Stripe ships X-Frame-Options: DENY.)
  const taxRate = saved.taxRate || 7;
  const tierWithTotals = tiers.map(t => {
    const sub = (t.lines||[]).reduce((s,l)=>s+(Number(l.qty)||1)*(Number(l.price)||0),0);
    return { ...t, subtotal: sub, total: sub * (1 + taxRate/100) };
  });
  const openFull = () => { try { window.open(saved.portalUrl, '_blank', 'noopener'); } catch { window.location.href = saved.portalUrl; } };
  return <div>
    <div style={{ textAlign:'center', marginBottom:14 }}>
      <div style={{ fontSize:28, marginBottom:4 }}>📲</div>
      <div style={{ fontSize:18, fontWeight:800 }}>Show this to {saved.customerName || 'the customer'}</div>
      <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>They also got the link by text/email — same thing.</div>
    </div>

    {/* Inline tier summary cards — no iframe, so Stripe loads fine when the
        tech opens the full view below to take payment on the spot. */}
    {tierWithTotals.length > 0 && <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:14 }}>
      {tierWithTotals.map((t, i) => {
        const accent = i === 0 ? G.cyan : i === 1 ? G.purple : G.green;
        const active = selectedTier === t.key;
        return <div key={t.key} onClick={() => pickTier(t.key)} style={{
          padding:12, borderRadius:10, cursor:'pointer',
          background: active ? accent + '22' : G.input,
          border: `2px solid ${active ? accent : G.cardBorder}`,
          transition: 'transform .12s',
          transform: active ? 'translateY(-2px)' : 'none',
        }}>
          <div style={{ fontSize:10, fontWeight:800, color:accent, textTransform:'uppercase', letterSpacing:'.05em' }}>
            {i === 0 ? 'Good' : i === 1 ? 'Better' : 'Best'}
          </div>
          <div style={{ fontWeight:700, fontSize:13, marginTop:2, color:G.text }}>{t.name}</div>
          {t.label && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{t.label}</div>}
          <div style={{ fontSize:18, fontWeight:800, color:accent, marginTop:8 }}>${t.total.toFixed(0)}</div>
          <div style={{ fontSize:10, color:G.textMuted }}>{(t.lines||[]).length} item{(t.lines||[]).length===1?'':'s'}</div>
        </div>;
      })}
    </div>}

    {/* Open the full customer view in a new tab — Stripe / signature / pay
        all work there because we're not in an iframe anymore. */}
    <div style={{ marginBottom:14 }}>
      <Btn sz="lg" full onClick={openFull} v="cyan">📲 Open Full Estimate View ↗</Btn>
      <div style={{ fontSize:11, color:G.textMuted, textAlign:'center', marginTop:6 }}>
        Opens in a new tab — hand the phone to the customer to sign + pay.
      </div>
    </div>

    {/* (Tier-picker chips moved to the inline grid above — tap a card to select.) */}

    {/* Actions — bottom padding clears the TechApp fixed bottom nav (~90px
        including iPhone home-bar safe area) so the last button is tappable. */}
    <div style={{ display:'grid', gap:8, paddingBottom:'140px' }}>
      <Btn v="primary" sz="lg" disabled={!selectedTier || savingTier} onClick={async () => {
        // One tap: mark estimate approved, create invoice from selected tier,
        // auto-send it to the customer's phone + email. Then bounce back to
        // start so the tech can move to the next call.
        setSavingTier(true);
        try {
          const r = await api(`/api/estimates/${saved.id}/convert`, { method:'POST', body:{ tierKey: selectedTier, autoSend: true }});
          alert(`✓ Invoice ${r.invoice?.num} sent to customer${r.sent?.sms ? ' (SMS)' : ''}${r.sent?.email ? ' + email' : ''}.`);
          onDone();
        } catch (e) { alert('Convert failed: ' + e.message); }
        finally { setSavingTier(false); }
      }}>
        ✓ Customer approved → Send Invoice
      </Btn>
      {onGetSignature && <Btn sz="lg" onClick={onGetSignature}>✍️ Get a signature first</Btn>}
      <Btn sz="sm" v="ghost" onClick={copyLink}>📋 Copy customer link</Btn>
      <Btn v="ghost" sz="sm" onClick={onDone}>← Done for now</Btn>
    </div>
  </div>;
}

// ─── Floating Action Button for owner shell ─────────────────────────────────
export function QuickCreateFab({ toast }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return <button onClick={()=>setOpen(true)} aria-label="Quick Create" style={{
      position:'fixed', bottom:24, right:24, zIndex:900,
      width:56, height:56, borderRadius:28, border:'none', cursor:'pointer',
      background:`linear-gradient(135deg, ${G.purple}, ${G.blue})`, color:'#fff',
      fontSize:24, fontWeight:700, boxShadow:'0 6px 20px rgba(139,92,246,0.5)',
    }}>+</button>;
  }
  return <>
    <div onClick={()=>setOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:950 }}/>
    <div style={{
      position:'fixed', top:0, right:0, bottom:0,
      width:'min(460px, 100vw)', // full-width on phones, capped on tablets+
      background:G.card, borderLeft:`1px solid ${G.border}`, zIndex:951,
      padding:'22px 18px calc(80px + env(safe-area-inset-bottom, 0px))',
      overflowY:'auto', WebkitOverflowScrolling:'touch',
      boxShadow:'-8px 0 32px rgba(0,0,0,.4)',
    }}>
      <QuickCreatePanel toast={toast} onCreated={()=>setOpen(false)}/>
    </div>
  </>;
}
